import {FC, useEffect, useState} from 'react'
import axios from 'axios'
import {Modal} from 'react-bootstrap'
import {apiUrl} from '../../../api-utils/constantVariables'
import {AdminAuth} from '../../modules/auth/core/AdminAuth'

export type AdminRow = {
  id: number
  email: string
  name?: string
  is_active?: boolean
  totp_enabled?: boolean
  role_id?: number
  role?: string
  assignedRole?: {id: number; code: string; name: string}
}

export type InviteRow = {
  id: number
  email: string
  name?: string | null
  role_id: number
  expires_at: string
  role?: {code: string; name: string}
}

type Props = {
  show: boolean
  onHide: () => void
  onDone: () => void
  mode: 'admin' | 'invite'
  admin?: AdminRow | null
  invite?: InviteRow | null
}

type RoleRow = {id: number; code: string; name: string}

export const EditAdminModal: FC<Props> = ({show, onHide, onDone, mode, admin, invite}) => {
  const [roles, setRoles] = useState<RoleRow[]>([])
  const [roleId, setRoleId] = useState(0)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!show) return
    const token = AdminAuth()
    if (!token) return
    void axios
      .get(`${apiUrl}/admin/roles`, {headers: {'x-access-token': token}})
      .then((res) => {
        const rows = res.data?.data as RoleRow[] | undefined
        setRoles(Array.isArray(rows) ? rows : [])
      })
      .catch(() => setRoles([]))
  }, [show])

  useEffect(() => {
    if (!show) return
    if (mode === 'admin' && admin) {
      setRoleId(admin.role_id || admin.assignedRole?.id || 0)
    }
    setErr('')
  }, [show, mode, admin, invite])

  const tokenHeaders = () => ({headers: {'x-access-token': AdminAuth()}})

  const saveRole = async () => {
    if (!admin || !roleId) return
    setBusy(true)
    setErr('')
    try {
      await axios.post(`${apiUrl}/admin/users/${admin.id}/role`, {role_id: roleId}, tokenHeaders())
      onDone()
      onHide()
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Update failed.'
      setErr(typeof msg === 'string' ? msg : 'Update failed.')
    } finally {
      setBusy(false)
    }
  }

  const deactivate = async () => {
    if (!admin) return
    if (!window.confirm('Deactivate this admin?')) return
    setBusy(true)
    setErr('')
    try {
      await axios.post(`${apiUrl}/admin/users/${admin.id}/deactivate`, {}, tokenHeaders())
      onDone()
      onHide()
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Request failed.'
      setErr(typeof msg === 'string' ? msg : 'Request failed.')
    } finally {
      setBusy(false)
    }
  }

  const reactivate = async () => {
    if (!admin) return
    setBusy(true)
    setErr('')
    try {
      await axios.post(`${apiUrl}/admin/users/${admin.id}/reactivate`, {}, tokenHeaders())
      onDone()
      onHide()
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Request failed.'
      setErr(typeof msg === 'string' ? msg : 'Request failed.')
    } finally {
      setBusy(false)
    }
  }

  const resendInvite = async () => {
    if (!invite) return
    setBusy(true)
    setErr('')
    try {
      await axios.post(`${apiUrl}/admin/users/invites/${invite.id}/resend`, {}, tokenHeaders())
      onDone()
      onHide()
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Request failed.'
      setErr(typeof msg === 'string' ? msg : 'Request failed.')
    } finally {
      setBusy(false)
    }
  }

  const cancelInvite = async () => {
    if (!invite) return
    if (!window.confirm('Cancel this invite?')) return
    setBusy(true)
    setErr('')
    try {
      await axios.post(`${apiUrl}/admin/users/invites/${invite.id}/cancel`, {}, tokenHeaders())
      onDone()
      onHide()
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Request failed.'
      setErr(typeof msg === 'string' ? msg : 'Request failed.')
    } finally {
      setBusy(false)
    }
  }

  const title =
    mode === 'admin' ? `Admin — ${admin?.email || ''}` : `Pending invite — ${invite?.email || ''}`

  return (
    <Modal show={show} onHide={onHide} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err ? <div className='alert alert-danger'>{err}</div> : null}
        {mode === 'admin' && admin ? (
          <>
            <div className='row mb-5'>
              <div className='col-md-6'>
                <div className='text-muted fs-7'>Name</div>
                <div className='fw-semibold'>{admin.name || '—'}</div>
              </div>
              <div className='col-md-6'>
                <div className='text-muted fs-7'>2FA</div>
                <div className='fw-semibold'>{admin.totp_enabled ? 'Enabled' : 'Off'}</div>
              </div>
            </div>
            <div className='mb-5'>
              <label className='form-label'>Role</label>
              <select
                className='form-select'
                value={roleId || ''}
                onChange={(e) => setRoleId(+e.target.value)}
              >
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.code})
                  </option>
                ))}
              </select>
            </div>
            <div className='d-flex flex-wrap gap-2'>
              <button type='button' className='btn btn-primary' disabled={busy} onClick={saveRole}>
                Save role
              </button>
              {admin.is_active ? (
                <button
                  type='button'
                  className='btn btn-light-danger'
                  disabled={busy}
                  onClick={deactivate}
                >
                  Deactivate
                </button>
              ) : (
                <button
                  type='button'
                  className='btn btn-light-success'
                  disabled={busy}
                  onClick={reactivate}
                >
                  Reactivate
                </button>
              )}
            </div>
          </>
        ) : null}

        {mode === 'invite' && invite ? (
          <>
            <p className='text-gray-700'>
              Role: <strong>{invite.role?.name || invite.role_id}</strong>
            </p>
            <p className='text-muted fs-7'>
              Expires: {new Date(invite.expires_at).toLocaleString()}
            </p>
            <div className='d-flex flex-wrap gap-2'>
              <button
                type='button'
                className='btn btn-primary'
                disabled={busy}
                onClick={resendInvite}
              >
                Resend email
              </button>
              <button
                type='button'
                className='btn btn-light-danger'
                disabled={busy}
                onClick={cancelInvite}
              >
                Cancel invite
              </button>
            </div>
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  )
}
